import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Card } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';

interface AdminUser {
  id: number;
  username: string;
  display_name: string;
  email: string;
  status: string;
  email_verified: boolean;
  roles: string[];
  created_at: string;
  last_seen_at: string | null;
}

interface Page {
  items: AdminUser[];
  next_cursor: string | null;
}

export function AdminUsersPage() {
  const [search, setSearch] = useState('');
  const me = useSession((s) => s.user);

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'users', search],
    queryFn: () => api.get<Page>('/admin/users', { query: { q: search || undefined, limit: 50 } }),
  });

  return (
    <div className="flex flex-col gap-5">

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        <p className="mt-1 text-sm text-soft">Accounts, roles and suspension.</p>
      </div>

      <Field
        // "Search users", not "Search": the top bar already has a control called
        // Search, and two identically-named controls on one page are ambiguous
        // to anybody navigating by label rather than by sight.
        label="Search users"
        placeholder="username or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        hint="Matches from the start of a username or address."
      />

      {isPending && <p className="text-soft">Loading…</p>}
      {error && <Alert tone="error">Could not load users.</Alert>}

      {data && (
        <Card className="divide-y divide-line">
          {data.items.length === 0 && <p className="p-6 text-sm text-soft">No accounts match.</p>}

          {data.items.map((user) => (
            /*
             * A link, not an action surface.
             *
             * This row used to carry Suspend and a hardcoded "Make mod". That
             * shape has one bug per role - there was no way to revoke hosting
             * from the admin panel at all, because nobody had added a button
             * for it - and it offered decisions without any of the context a
             * decision needs. A list is for finding somebody; deciding about
             * them happens on their own page.
             */
            <Link
              key={user.id}
              to={`/admin/users/${user.id}`}
              // A stable hook for the browser tests. Selecting the row by its
              // text matches every ancestor that contains that text too, which
              // makes assertions about one row quietly wrong.
              data-username={user.username}
              className="flex flex-wrap items-center gap-4 p-4 transition-colors hover:bg-raised"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{user.display_name}</span>
                  <span className="text-sm text-faint">@{user.username}</span>
                  {user.id === me?.id && (
                    <span className="rounded bg-raised px-1.5 py-0.5 text-xs text-faint">you</span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-sm text-faint">{user.email}</div>
              </div>

              <div className="flex items-center gap-1.5">
                {user.status !== 'active' && (
                  <span className="rounded bg-hot/15 px-2 py-0.5 text-xs text-hot-text">
                    {user.status}
                  </span>
                )}
                {!user.email_verified && (
                  <span className="rounded bg-raised px-2 py-0.5 text-xs text-faint">unverified</span>
                )}
                {user.roles.map((name) => (
                  <span key={name} className="rounded bg-accent/15 px-2 py-0.5 text-xs text-accent">
                    {name}
                  </span>
                ))}
              </div>

              <span aria-hidden className="text-faint">
                &rsaquo;
              </span>
            </Link>
          ))}
        </Card>
      )}

      {data?.next_cursor && (
        <p className="text-center text-sm text-faint">
          More results — narrow the search to find a specific account.
        </p>
      )}
    </div>
  );
}
